/**
 * Capa 4: Hook Orquestador del Asistente de Onboarding — useOnboardingWizard
 * Coordina navegación, sincronización en Redis, validaciones Zod, anti-colisión y Zero-Friction Handover.
 */

'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import confetti from 'canvas-confetti';
import { toast } from 'sonner';
import { useAuth } from '@/core/auth/auth-context';
import { ROUTES } from '@/config/routes';
import { normalizeApiError } from '@/core/exceptions/api-errors';
import { onboardingApi } from '../api/onboarding.api';
import { useOnboardingStore } from '../store/onboarding.store';
import { TipoErrorOnboarding } from '../components/OnboardingErrorModal';
import {
  paso1GranjaSchema,
  paso2UsuarioSchema,
  step2UsuarioFormSchema,
  paso3CredencialesSchema,
} from '../schemas/onboarding.schema';
import {
  Paso1GranjaFormData,
  Paso2UsuarioFormData,
  Paso3CredencialesFormData,
} from '../types/onboarding.types';

export interface ErrorModalState {
  isOpen: boolean;
  tipo: TipoErrorOnboarding;
  titulo?: string;
  mensaje?: string;
  detalleContextual?: string;
  onAccionPrincipal?: () => void;
  onAccionSecundaria?: () => void;
}

export function useOnboardingWizard() {
  const router = useRouter();
  const { login } = useAuth();
  const initializedRef = useRef(false);

  const {
    idOnboarding,
    pasoActual,
    direccionAnimacion,
    paso1,
    paso2,
    paso3,
    emailValidation,
    telefonoValidation,
    isInitializing,
    isSubmitting,
    setIdOnboarding,
    setPasoActual,
    updatePaso1,
    updatePaso2,
    updatePaso3,
    setEmailValidation,
    setTelefonoValidation,
    setIsInitializing,
    setIsSubmitting,
    resetOnboarding,
  } = useOnboardingStore();

  const [errorModal, setErrorModal] = useState<ErrorModalState>({
    isOpen: false,
    tipo: 'GENERICO',
  });

  const resetValidaciones = useCallback(() => {
    setEmailValidation({ checking: false, available: true, suggestLogin: false, message: null });
    setTelefonoValidation({ checking: false, available: true, suggestLogin: false, message: null });
  }, [setEmailValidation, setTelefonoValidation]);

  const cerrarErrorModal = useCallback(() => {
    resetValidaciones();
    setErrorModal((prev) => ({ ...prev, isOpen: false }));
  }, [resetValidaciones]);

  const mostrarErrorModal = useCallback(
    (err: any, retryAction?: () => void) => {
      const normErr = normalizeApiError(err);
      const status = err?.status || err?.response?.status || (normErr as any)?.status;
      const message = normErr.message || '';

      if (status === 409 || message.toLowerCase().includes('ya se encuentra registrado')) {
        setErrorModal({
          isOpen: true,
          tipo: 'DUPLICADO',
          mensaje: err?.response?.data?.detail || normErr.message || 'El correo electrónico o WhatsApp suministrado ya está registrado.',
          detalleContextual: err?.detalle || err?.response?.data?.detail || paso3.email || paso2.email || paso2.whatsapp,
          onAccionPrincipal: () => {
            setErrorModal((prev) => ({ ...prev, isOpen: false }));
            router.push(ROUTES.AUTH.LOGIN);
          },
          onAccionSecundaria: () => {
            resetValidaciones();
            setErrorModal((prev) => ({ ...prev, isOpen: false }));
            if (pasoActual === 3) {
              setPasoActual(2, -1);
            }
          },
        });
        return;
      }

      if (status === 404 || message.toLowerCase().includes('expirad')) {
        setErrorModal({
          isOpen: true,
          tipo: 'EXPIRADO',
          onAccionPrincipal: () => {
            resetOnboarding();
            setPasoActual(1, -1);
            setErrorModal((prev) => ({ ...prev, isOpen: false }));
          },
          onAccionSecundaria: () => {
            setErrorModal((prev) => ({ ...prev, isOpen: false }));
            window.open(
              'https://wa.me/573000000000?text=Hola%20AviF%C3%A1cil,%20mi%20sesion%20de%20registro%20ha%20expirado',
              '_blank'
            );
          },
        });
        return;
      }

      if (status === 429) {
        setErrorModal({
          isOpen: true,
          tipo: 'RATE_LIMIT',
          onAccionPrincipal: () => {
            setErrorModal((prev) => ({ ...prev, isOpen: false }));
          },
        });
        return;
      }

      if (err?.isAxiosError && !err.response) {
        setErrorModal({
          isOpen: true,
          tipo: 'CONEXION',
          onAccionPrincipal: () => {
            setErrorModal((prev) => ({ ...prev, isOpen: false }));
            if (retryAction) retryAction();
          },
          onAccionSecundaria: () => {
            setErrorModal((prev) => ({ ...prev, isOpen: false }));
          },
        });
        return;
      }

      // Fallo de transacción (500) o genérico
      setErrorModal({
        isOpen: true,
        tipo: status === 500 ? 'TRANSACCION' : 'GENERICO',
        mensaje: message,
        onAccionPrincipal: () => {
          setErrorModal((prev) => ({ ...prev, isOpen: false }));
          if (retryAction) retryAction();
        },
        onAccionSecundaria: () => {
          setErrorModal((prev) => ({ ...prev, isOpen: false }));
          window.open(
            'https://wa.me/573000000000?text=Hola%20AviF%C3%A1cil,%20necesito%20ayuda%20con%20el%20alta%20de%20mi%20granja',
            '_blank'
          );
        },
      });
    },
    [paso2.email, paso2.whatsapp, paso3.email, pasoActual, resetOnboarding, resetValidaciones, router, setPasoActual]
  );

  /**
   * 1. Apertura o Recuperación de Sesión Temporal en Redis
   */
  const inicializarSesion = useCallback(async () => {
    if (initializedRef.current) return;
    initializedRef.current = true;

    setIsInitializing(true);
    try {
      if (idOnboarding) {
        // Intenta recuperar sesión previa si Don Carlos recargó el navegador
        try {
          const sesion = await onboardingApi.obtenerSesion(idOnboarding);
          if (sesion && sesion.activo) {
            if (sesion.datos_acumulados?.paso_1) {
              updatePaso1(sesion.datos_acumulados.paso_1);
            }
            if (sesion.datos_acumulados?.paso_2) {
              updatePaso2(sesion.datos_acumulados.paso_2);
              if (sesion.datos_acumulados.paso_2.email) {
                updatePaso3({
                  email: sesion.datos_acumulados.paso_2.email,
                  telefono_movil: sesion.datos_acumulados.paso_2.whatsapp
                    ? `${sesion.datos_acumulados.paso_2.codigo_pais_whatsapp || '+57'} ${sesion.datos_acumulados.paso_2.whatsapp}`
                    : undefined,
                });
              }
            }
            if (sesion.datos_acumulados?.paso_3) {
              updatePaso3(sesion.datos_acumulados.paso_3);
            }
            if (sesion.paso_actual === 'PASO_2_USUARIO') {
              setPasoActual(2, 1);
            } else if (sesion.paso_actual === 'PASO_3_CREDENCIALES') {
              setPasoActual(3, 1);
            }
            setIsInitializing(false);
            return;
          }
        } catch {
          // Si expiró el TTL en Redis (2h), creamos un nuevo ticket
          setIdOnboarding(null);
        }
      }

      // Nueva sesión de alta
      const initRes = await onboardingApi.inicializar('WEB_SPA', 'ORGANICO');
      setIdOnboarding(initRes.id_onboarding);
    } catch (err) {
      const normErr = normalizeApiError(err);
      toast.error(normErr.message || 'Error al iniciar el asistente de registro');
    } finally {
      setIsInitializing(false);
    }
  }, [idOnboarding, setIdOnboarding, setPasoActual, updatePaso1, updatePaso2, setIsInitializing]);

  useEffect(() => {
    inicializarSesion();
  }, [inicializarSesion]);

  /**
   * 2. Avance Paso 1: Predio Avícola
   */
  const guardarPaso1 = useCallback(
    async (data: Paso1GranjaFormData): Promise<boolean> => {
      const validation = paso1GranjaSchema.safeParse(data);
      if (!validation.success) {
        const primerError = validation.error.issues[0]?.message || 'Datos de granja inválidos';
        toast.error(primerError);
        return false;
      }

      updatePaso1(validation.data);

      if (idOnboarding) {
        try {
          await onboardingApi.actualizarPaso(idOnboarding, 'PASO_1_GRANJA', validation.data);
        } catch (err) {
          const normErr = normalizeApiError(err);
          toast.warning(`Progreso guardado localmente (${normErr.message})`);
        }
      }

      setPasoActual(2, 1);
      return true;
    },
    [idOnboarding, updatePaso1, setPasoActual]
  );

  /**
   * 3. Avance Paso 2: Titular Legal
   */
  const guardarPaso2 = useCallback(
    async (data: Paso2UsuarioFormData): Promise<boolean> => {
      const validation = step2UsuarioFormSchema.safeParse(data);
      if (!validation.success) {
        const primerError = validation.error.issues[0]?.message || 'Datos personales inválidos';
        toast.error(primerError);
        return false;
      }

      // Verificación anti-colisión en vivo antes de permitir pasar a Seguridad
      if (validation.data.email) {
        try {
          const res = await onboardingApi.validarIdentificador('EMAIL', validation.data.email);
          if (!res.disponible) {
            setEmailValidation({
              checking: false,
              available: false,
              suggestLogin: true,
              message: res.mensaje || 'Este correo ya se encuentra registrado',
            });
            mostrarErrorModal({
              status: 409,
              detalle: validation.data.email,
              response: {
                status: 409,
                data: { detail: res.mensaje || `El correo ${validation.data.email} ya se encuentra registrado en AviFácil.` },
              },
            });
            return false;
          } else {
            setEmailValidation({
              checking: false,
              available: true,
              suggestLogin: false,
              message: null,
            });
          }
        } catch {
          // Continuar en caso de fallo de red
        }
      }

      if (validation.data.whatsapp) {
        try {
          const telCompleto = `${validation.data.codigo_pais_whatsapp || '+57'} ${validation.data.whatsapp}`;
          const resTel = await onboardingApi.validarIdentificador('TELEFONO', telCompleto);
          if (!resTel.disponible) {
            setTelefonoValidation({
              checking: false,
              available: false,
              suggestLogin: true,
              message: resTel.mensaje || 'Este número de WhatsApp ya se encuentra registrado',
            });
            mostrarErrorModal({
              status: 409,
              detalle: telCompleto,
              response: {
                status: 409,
                data: { detail: resTel.mensaje || `El WhatsApp ${telCompleto} ya se encuentra registrado en AviFácil.` },
              },
            });
            return false;
          } else {
            setTelefonoValidation({
              checking: false,
              available: true,
              suggestLogin: false,
              message: null,
            });
          }
        } catch {
          // Continuar
        }
      }

      updatePaso2(validation.data);

      if (validation.data.email) {
        updatePaso3({
          email: validation.data.email,
          telefono_movil: validation.data.whatsapp
            ? `${validation.data.codigo_pais_whatsapp || '+57'} ${validation.data.whatsapp}`
            : undefined,
        });
      }

      if (idOnboarding) {
        try {
          await onboardingApi.actualizarPaso(idOnboarding, 'PASO_2_USUARIO', validation.data);
        } catch (err) {
          const normErr = normalizeApiError(err);
          toast.warning(`Progreso guardado localmente (${normErr.message})`);
        }
      }

      setPasoActual(3, 1);
      return true;
    },
    [idOnboarding, updatePaso2, updatePaso3, setPasoActual, setEmailValidation, setTelefonoValidation, mostrarErrorModal]
  );

  /**
   * 4. Retroceso de Paso
   */
  const retrocederPaso = useCallback(() => {
    if (pasoActual > 1) {
      setPasoActual(pasoActual - 1, -1);
    }
  }, [pasoActual, setPasoActual]);

  /**
   * 5. Centinela Anti-Colisión en Debounce
   */
  const validarEmailAntiColision = useCallback(
    async (email: string) => {
      const trimmed = email.trim().toLowerCase();
      if (!trimmed || !trimmed.includes('@') || !trimmed.includes('.')) {
        setEmailValidation({ checking: false, available: true, suggestLogin: false, message: null });
        return;
      }

      setEmailValidation({ checking: true });
      try {
        const res = await onboardingApi.validarIdentificador('EMAIL', trimmed);
        setEmailValidation({
          checking: false,
          available: res.disponible,
          suggestLogin: res.sugerir_login,
          message: res.mensaje || (res.disponible ? null : 'Este correo ya se encuentra registrado'),
        });
      } catch {
        setEmailValidation({ checking: false, available: true, suggestLogin: false, message: null });
      }
    },
    [setEmailValidation]
  );

  /**
   * 6. Finalización Transaccional ACID y Zero-Friction Token Handover
   */
  const completarRegistro = useCallback(
    async (data: Paso3CredencialesFormData): Promise<boolean> => {
      const emailFinal = (data.email || paso3.email || paso2.email || '').trim().toLowerCase();
      const telefonoFinal =
        data.telefono_movil ||
        paso3.telefono_movil ||
        (paso2.whatsapp
          ? `${paso2.codigo_pais_whatsapp || '+57'} ${paso2.whatsapp}`
          : undefined);

      const dataCompleta: Paso3CredencialesFormData = {
        ...data,
        email: emailFinal,
        telefono_movil: telefonoFinal,
      };

      const validation = paso3CredencialesSchema.safeParse(dataCompleta);
      if (!validation.success) {
        const primerError = validation.error.issues[0]?.message || 'Credenciales inválidas';
        toast.error(primerError);
        return false;
      }

      if (emailValidation.suggestLogin || !emailValidation.available) {
        mostrarErrorModal(
          {
            status: 409,
            response: { status: 409, data: { detail: 'Este correo ya se encuentra registrado' } },
          },
          () => completarRegistro(dataCompleta)
        );
        return false;
      }

      updatePaso3(validation.data);
      setIsSubmitting(true);

      try {
        // Enviar Paso 3 a Redis
        if (idOnboarding) {
          try {
            await onboardingApi.actualizarPaso(idOnboarding, 'PASO_3_CREDENCIALES', {
              email: validation.data.email,
              telefono_movil: validation.data.telefono_movil,
              password: validation.data.password,
              acepta_terminos_y_condiciones: validation.data.acepta_terminos_y_condiciones,
            });
          } catch {
            // Continúa para intentar el completar con datos completos
          }
        }

        // Ejecutar Transacción Atómica en PostgreSQL (Granja + Usuario + Sesión + Evento)
        const payloadCompleto = {
          id_onboarding: idOnboarding || undefined,
          datos_completos: {
            granja: paso1,
            usuario: {
              ...paso2,
              email: emailFinal,
            },
            credenciales: {
              email: validation.data.email,
              telefono_movil: validation.data.telefono_movil,
              password: validation.data.password,
              acepta_terminos_y_condiciones: validation.data.acepta_terminos_y_condiciones,
            },
          },
        };

        const res = await onboardingApi.completar(payloadCompleto);

        // Zero-Friction Token Handover: Cargar tokens directamente en AuthContext
        login(
          res.tokens.access_token,
          res.tokens.refresh_token,
          {
            id: res.usuario.id_usuario,
            email: res.usuario.email,
            nombre: res.usuario.nombre_display,
            farm_id: res.granja.id_granja,
            granja_id: res.granja.id_granja,
            rol: res.usuario.rol_sistema,
          },
          true
        );

        // Celebración Visual con Confetti
        try {
          confetti({
            particleCount: 120,
            spread: 75,
            origin: { y: 0.6 },
            colors: ['#78A83B', '#1C1938', '#4A6B28', '#FFD700'],
          });
        } catch {
          // Confetti pasivo
        }

        toast.success(`¡Bienvenido a AviFácil, ${res.usuario.nombre_display}!`, {
          description: `Granja "${res.granja.nombre}" aprovisionada exitosamente.`,
          duration: 4000,
        });

        // Transición a la pantalla de bienvenida con regalo (Paso 4)
        setPasoActual(4, 1);
        return true;
      } catch (err: any) {
        const normErr = normalizeApiError(err);
        toast.error(normErr.message || 'Error al completar el registro. Intenta nuevamente.');
        mostrarErrorModal(err, () => completarRegistro(data));
        return false;
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      idOnboarding,
      paso1,
      paso2,
      emailValidation,
      updatePaso3,
      setIsSubmitting,
      login,
      setPasoActual,
    ]
  );

  /**
   * 6.1 Finalizar Onboarding e Ingresar a la Granja
   */
  const finalizarOnboarding = useCallback(() => {
    resetOnboarding();
    router.push(ROUTES.GRANJA);
  }, [resetOnboarding, router]);

  /**
   * 7. Salida o Deserción Voluntaria
   */
  const abandonarOnboarding = useCallback(async () => {
    if (idOnboarding) {
      try {
        await onboardingApi.abandonar({
          id_onboarding: idOnboarding,
          ultimo_paso: `PASO_${pasoActual}`,
          motivo_inferido: 'SALIDA_VOLUNTARIA_USUARIO',
        });
      } catch {
        // Silencioso
      }
    }
    resetOnboarding();
    router.push(ROUTES.HOME);
  }, [idOnboarding, pasoActual, resetOnboarding, router]);

  /**
   * 8. FN-ONB-CLI-05A: Autocompletar Wizard por Voz (Qwen-Audio ASR + Qwen LLM)
   */
  const autocompletarPorVoz = useCallback(
    async (params: { audio_base64?: string; mime_type?: string; texto_simulado?: string }) => {
      if (!idOnboarding) {
        toast.error('Iniciando sesión de registro, intenta en un segundo.');
        return null;
      }
      setIsSubmitting(true);
      try {
        const res = await onboardingApi.autocompletarPorVoz({
          id_onboarding: idOnboarding,
          audio_base64: params.audio_base64,
          mime_type: params.mime_type || 'audio/ogg',
          texto_simulado: params.texto_simulado,
        });
        if (res.datos_autocompletados?.paso_1) {
          updatePaso1(res.datos_autocompletados.paso_1);
        }
        if (res.datos_autocompletados?.paso_2) {
          updatePaso2(res.datos_autocompletados.paso_2);
        }
        if (res.datos_autocompletados?.contacto_detectado) {
          updatePaso3({
            email: res.datos_autocompletados.contacto_detectado.email || paso3.email,
            telefono_movil:
              res.datos_autocompletados.contacto_detectado.telefono_movil || paso3.telefono_movil,
          });
        }
        toast.success('Datos extraídos por voz con éxito', {
          description: `Transcripción: "${res.transcripcion}"`,
        });
        return res;
      } catch (err) {
        const normErr = normalizeApiError(err);
        toast.error(normErr.message || 'No se pudo procesar la nota de voz');
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [idOnboarding, paso3.email, paso3.telefono_movil, setIsSubmitting, updatePaso1, updatePaso2, updatePaso3]
  );

  /**
   * 9. FN-ONB-CLI-05B: Autocompletar Wizard por Foto/PDF de RUT DIAN o Certificado ICA
   */
  const autocompletarPorDocumento = useCallback(
    async (params: { documento_base64?: string; mime_type?: string; texto_documento?: string }) => {
      if (!idOnboarding) {
        toast.error('Iniciando sesión de registro, intenta en un segundo.');
        return null;
      }
      setIsSubmitting(true);
      try {
        const res = await onboardingApi.autocompletarPorDocumento({
          id_onboarding: idOnboarding,
          documento_base64: params.documento_base64,
          mime_type: params.mime_type || 'image/jpeg',
          texto_documento: params.texto_documento,
        });
        if (res.datos_autocompletados?.paso_1) {
          updatePaso1(res.datos_autocompletados.paso_1);
        }
        if (res.datos_autocompletados?.paso_2) {
          updatePaso2(res.datos_autocompletados.paso_2);
        }
        toast.success(
          `Documento ${res.tipo_documento_detectado} procesado (NIT: ${res.nit_extraido})`,
          {
            description: `Granja "${res.datos_autocompletados.paso_1.nombre_granja}" pre-cargada.`,
          }
        );
        return res;
      } catch (err) {
        const normErr = normalizeApiError(err);
        toast.error(normErr.message || 'No se pudo extraer la información del documento');
        return null;
      } finally {
        setIsSubmitting(false);
      }
    },
    [idOnboarding, setIsSubmitting, updatePaso1, updatePaso2]
  );

  /**
   * 10. FN-ONB-CLI-09: Rehidratar sesión desde Magic Link (?resume_token=)
   */
  const recuperarDesdeMagicLink = useCallback(
    async (token: string): Promise<boolean> => {
      if (!token) return false;
      setIsInitializing(true);
      try {
        const sesion = await onboardingApi.recuperarDesdeMagicLink(token);
        setIdOnboarding(sesion.id_onboarding);
        if (sesion.datos_acumulados?.paso_1) {
          updatePaso1(sesion.datos_acumulados.paso_1);
        }
        if (sesion.datos_acumulados?.paso_2) {
          updatePaso2(sesion.datos_acumulados.paso_2);
        }
        if (sesion.datos_acumulados?.paso_3) {
          updatePaso3(sesion.datos_acumulados.paso_3);
        }
        if (sesion.paso_actual === 'PASO_2_USUARIO') {
          setPasoActual(2, 1);
        } else if (sesion.paso_actual === 'PASO_3_CREDENCIALES') {
          setPasoActual(3, 1);
        } else {
          setPasoActual(1, 1);
        }
        toast.success('Tu progreso de registro fue restaurado desde el enlace mágico.');
        return true;
      } catch (err) {
        const normErr = normalizeApiError(err);
        toast.error(normErr.message || 'El enlace de recuperación expiró o no es válido');
        return false;
      } finally {
        setIsInitializing(false);
      }
    },
    [setIdOnboarding, setIsInitializing, setPasoActual, updatePaso1, updatePaso2, updatePaso3]
  );

  return {
    idOnboarding,
    pasoActual,
    direccionAnimacion,
    paso1,
    paso2,
    paso3,
    emailValidation,
    telefonoValidation,
    isInitializing,
    isSubmitting,
    guardarPaso1,
    guardarPaso2,
    retrocederPaso,
    validarEmailAntiColision,
    completarRegistro,
    finalizarOnboarding,
    abandonarOnboarding,
    autocompletarPorVoz,
    autocompletarPorDocumento,
    recuperarDesdeMagicLink,
    updatePaso1,
    updatePaso2,
    updatePaso3,
    setPasoActual,
    errorModal,
    cerrarErrorModal,
    mostrarErrorModal,
    resetValidaciones,
  };
}
